/**
 * ProgressSection.tsx - Fundraiser and Promoter Progress Display Component
 *
 * This component displays fundraising progress with dual progress bars for both
 * overall fundraiser progress and individual promoter progress. Supports multiple
 * layout modes and responsive design.
 *
 * Key Features:
 * - Dual progress display (fundraiser + promoter)
 * - Responsive layout with stacking options
 * - Status badges (pending, expiration warnings)
 * - Rank display for promoters
 * - Custom color theming support
 * - Conditional visibility based on builder settings
 *
 * Props:
 * - showFundraiser?: boolean - Whether to show fundraiser progress
 * - showPromoter?: boolean - Whether to show promoter progress
 * - promoterId?: string - Specific promoter ID to display
 * - hideExpirationBadge?: boolean - Hide expiration badges (for admin pages)
 * - forceStacked?: boolean - Force vertical stacking layout
 *
 * Dependencies: MobX store, badge utilities, Lucide icons
 */

import React from "react";
import styled from "styled-components";
import { Target, User } from "lucide-react";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../stores/FundraiserStore";
import {
  getExpirationBadgeText,
  shouldShowExpirationBadge,
  getPendingBadgeText,
  shouldShowPendingBadge,
} from "../utils/badgesUtils";
import { Fundraiser } from "../models/FundraisersGridModels";
import { FundRaiserStatus } from "../enums";
import { CreateFundraiserModel, PromoterProgressModel } from "../models";

const ProgressContainer = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns}, 1fr)`};
  gap: 24px;
  margin-top: 0px;
  width: 100%;
  &.progress-container {
    display: flex;
    .progress-card {
      width: 100%;
    }
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ProgressCard = styled.div<{ customColors?: any }>`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const ProgressHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PendingBadge = styled.div`
  background: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #93c5fd;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ExpirationBadge = styled.div`
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  white-space: nowrap;
  flex-shrink: 0;
`;
const ProgressIcon = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const ProgressTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const ProgressSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
`;

const AmountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 16px;
`;

const CurrentAmount = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
`;

const GoalAmount = styled.div`
  font-size: 16px;
  color: #64748b;
  font-weight: 600;
`;

const ProgressBarContainer = styled.div<{ customColors?: any }>`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const ProgressBar = styled.div<{
  percentage: number;
  color: string;
  customColors?: any;
}>`
  width: ${(props) => props.percentage}%;
  height: 100%;
  background: ${(props) => props.color};
  border-radius: 4px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  animation: progressLoadDashboard 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes progressLoadDashboard {
    from {
      width: 0%;
    }
    to {
      width: ${(props) => props.percentage}%;
    }
  }
`;

const ProgressFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressPercent = styled.div<{ color: string; customColors?: any }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.color};
`;

const RemainingAmount = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const RankBadge = styled.div`
  background: #10b981;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 8px;
  margin-left: 12px;
  flex-shrink: 0;
`;

const ProgressTitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

interface ProgressSectionProps {
  showFundraiser?: boolean;
  showPromoter?: boolean;
  promoterId?: string;
  hideExpirationBadge?: boolean; // New prop to hide expiration badge on admin pages
  forceStacked?: boolean; // New prop to force stacked layout
  promoterProgress?: PromoterProgressModel;
  fundraiser: CreateFundraiserModel;
}

export const ProgressSection = observer(
  ({
    promoterProgress,
    fundraiser,
    showFundraiser = true,
    showPromoter = true,
    promoterId,
    hideExpirationBadge = false,
    forceStacked = false,
  }: ProgressSectionProps) => {
    // Get builder data for section visibility
    const builderSectionVisibility =
      fundraiserStore.builderData?.sectionVisibility;

    const isBuilderPath = window.location.pathname.includes("builder");
    // Override props with builder visibility settings when in builder context
    const effectiveShowFundraiser = isBuilderPath
      ? builderSectionVisibility.fundraiserProgress !== false
      : showFundraiser;
    const effectiveShowPromoter = isBuilderPath
      ? builderSectionVisibility.promoterProgress !== false
      : showPromoter;

    // Check if we should force stacked layout (for RIGHT zone or explicit stacked setting)
    const progressLayout =
      fundraiserStore.builderData?.progressLayout || "side-by-side";
    const shouldStack = forceStacked || progressLayout === "stacked";

    // Handle Combined Progress logic - when combined is enabled, show both sections together
    const isCombinedProgress =
      window.location.pathname.includes("builder") &&
      builderSectionVisibility.progress === true;

    const hasLiveBuilderData = fundraiserStore.selectedFundraiserId === null;

    const goal = hasLiveBuilderData
      ? fundraiserStore.builderData.goal
      : fundraiserStore.goal;
    const selectedFundraiser = fundraiserStore.selectedFundraiser;

    const currentAmount = hasLiveBuilderData
      ? 0
      : promoterProgress?.totalRaisedAmount || 0;

    // const remainingAmount = hasLiveBuilderData
    //   ? promoterProgress?.goalAmount
    //   : fundraiserStore.remainingAmount;

    // If promoterId is provided, find that specific promoter, otherwise use current promoter
    let currentPromoter = promoterProgress;
    // if (promoterId) {
    //   const specificPromoter = fundraiserStore.promoters.find(
    //     (p) => p.id === promoterId
    //   );
    //   if (specificPromoter) {
    //     currentPromoter = specificPromoter;
    //   }
    // }

    const customColors = fundraiserStore.builderData?.customColors;

    // Get custom colors from fundraiser store if not provided as props
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    // Calculate if fundraiser expires within 7 days
    const shouldShowExpirationBadgeForFundraiser = () => {
      if (hideExpirationBadge) return false;
      return shouldShowExpirationBadge(
        selectedFundraiser?.endDate || null,
        selectedFundraiser?.status || FundRaiserStatus.Draft
      );
    };

    // Calculate if fundraiser is pending (start date > today)
    const shouldShowPendingBadgeForFundraiser = () => {
      if (hideExpirationBadge) return false; // Use same hide flag since it's for admin pages
      return shouldShowPendingBadge(
        selectedFundraiser?.startDate || null,
        selectedFundraiser?.status || ""
      );
    };

    // Badge priority logic: Pending takes priority over Expiration
    const shouldShowPendingBadgeInFundraiserProgress =
      shouldShowPendingBadgeForFundraiser();
    const shouldShowExpirationBadgeInFundraiserProgress =
      !shouldShowPendingBadgeForFundraiser() &&
      shouldShowExpirationBadgeForFundraiser();

    const fundraiserPercentage =
      fundraiser?.totalRaisedAmount === 0
        ? 0
        : (fundraiser?.totalRaisedAmount / fundraiser?.goal) * 100;
    const fundraiserBarPercentage = Math.min(fundraiserPercentage, 100);

    const promoterBarPercentage =
      currentPromoter && currentPromoter.goalAmount
        ? Math.min(
          (currentPromoter?.totalRaisedAmount / currentPromoter.goalAmount) *
          100,
          100
        )
        : 0;

    // Calculate actual percentages for display (can exceed 100%)
    let actualFundraiserPercentage =
      fundraiser?.totalRaisedAmount === 0
        ? 0
        : (fundraiser?.totalRaisedAmount / fundraiser?.goal) * 100;
    let actualPromoterPercentage =
      currentPromoter && currentPromoter.goalAmount
        ? (currentPromoter?.totalRaisedAmount / currentPromoter.goalAmount) *
        100
        : 0;

    actualFundraiserPercentage = actualFundraiserPercentage > 100 ? 100 : actualFundraiserPercentage;
    actualPromoterPercentage = actualPromoterPercentage > 100 ? 100 : actualPromoterPercentage;

    // Determine grid columns based on stacking
    // Calculate actual visible sections based on rendering conditions
    const actuallyShowFundraiser = effectiveShowFundraiser;
    const actuallyShowPromoter =
      effectiveShowPromoter && currentPromoter && currentPromoter?.id;
    const visibleSections = shouldStack
      ? 1
      : [actuallyShowFundraiser, actuallyShowPromoter].filter(Boolean).length;

    // If this is being called for Combined Progress, show both sections
    if (
      isCombinedProgress &&
      (effectiveShowFundraiser || effectiveShowPromoter)
    ) {
      // Force both to be visible when Combined Progress is active
      const finalShowFundraiser = true;
      const finalShowPromoter = true;
      const visibleSections = shouldStack ? 1 : 2; // Stack if forced or setting is stacked

      return (
        <ProgressContainer
          columns={visibleSections}
          className="progress-container"
        >
          <ProgressCard customColors={customColors} className="progress-card">
            {/* Fundraiser Progress Content */}
            <ProgressHeader>
              <ProgressHeaderLeft>
                <ProgressIcon color={customColors.primary.hex || "#3b82f6"}>
                  <Target size={18} />
                </ProgressIcon>
                <div>
                  <ProgressTitle>Fundraiser Progress</ProgressTitle>
                  <ProgressSubtitle>
                    {fundraiser?.activePromoters}
                  </ProgressSubtitle>
                </div>
              </ProgressHeaderLeft>
            </ProgressHeader>

            <AmountRow>
              <CurrentAmount>
                {formatCurrency(fundraiser?.totalRaisedAmount)}
              </CurrentAmount>
              <GoalAmount>of {formatCurrency(fundraiser?.goal)}</GoalAmount>
            </AmountRow>

            <ProgressBarContainer>
              <ProgressBar
                percentage={fundraiserBarPercentage}
                color={customColors?.primary.hex || "#3b82f6"}
                customColors={customColors}
              />
            </ProgressBarContainer>

            <ProgressFooter>
              <ProgressPercent
                color={customColors.primary.hex || "#3b82f6"}
                customColors={customColors}
              >
                {actualFundraiserPercentage.toFixed(1)}% Complete
              </ProgressPercent>
              {fundraiser?.remainingAmount() > 0 && (
                <RemainingAmount>
                  {formatCurrency(fundraiser?.remainingAmount())} to go
                </RemainingAmount>
              )}
            </ProgressFooter>
          </ProgressCard>

          {/* Add margin top when stacked */}
          {currentPromoter && (
            <ProgressCard
              customColors={customColors}
              style={{ marginTop: shouldStack ? "16px" : "0" }}
            >
              {/* Promoter Progress Content */}
              <ProgressHeader>
                <ProgressIcon color={customColors.secondary.hex || "#10b981"}>
                  <User size={18} />
                </ProgressIcon>
                <ProgressTitleContainer>
                  <div style={{ flex: 1 }}>
                    <ProgressTitle>
                      {currentPromoter?.displayName}'s Progress
                    </ProgressTitle>
                    <ProgressSubtitle>
                      Personal fundraising goal
                    </ProgressSubtitle>
                  </div>
                  {currentPromoter &&
                    currentPromoter?.totalRaisedAmount > 0 && (
                      <RankBadge>#{currentPromoter.rank || 2}</RankBadge>
                    )}
                </ProgressTitleContainer>
              </ProgressHeader>

              <AmountRow>
                <CurrentAmount>
                  {formatCurrency(currentPromoter?.totalRaisedAmount || 0)}
                </CurrentAmount>
                {currentPromoter.goalAmount > 0 && (
                  <GoalAmount>
                    of {formatCurrency(currentPromoter.goalAmount)}
                  </GoalAmount>
                )}
              </AmountRow>

              <ProgressBarContainer>
                <ProgressBar
                  percentage={promoterBarPercentage}
                  color={customColors.secondary.hex || "#10b981"}
                  customColors={customColors}
                />
              </ProgressBarContainer>

              <ProgressFooter>
                {currentPromoter.goalAmount > 0 && (
                  <ProgressPercent
                    color={customColors.secondary.hex || "#10b981"}
                    customColors={customColors}
                  >
                    {actualPromoterPercentage.toFixed(1)}% Complete
                  </ProgressPercent>
                )}
                {PromoterProgressModel.remainingAmount(
                  currentPromoter.goalAmount,
                  currentPromoter.totalRaisedAmount
                ) > 0 && (
                    <RemainingAmount>
                      {formatCurrency(
                        PromoterProgressModel.remainingAmount(
                          currentPromoter.goalAmount,
                          currentPromoter.totalRaisedAmount
                        )
                      )}{" "}
                      to go
                    </RemainingAmount>
                  )}
              </ProgressFooter>
            </ProgressCard>
          )}
        </ProgressContainer>
      );
    }

    // Don't render anything if no sections are visible
    if (visibleSections === 0) {
      return null;
    }

    return (
      <ProgressContainer
        columns={visibleSections}
        className="progress-containers"
      >
        {effectiveShowFundraiser && (
          <>
            <ProgressCard
              customColors={fundraiser?.customColors}
              className="progress-card"
            >
              <ProgressHeader>
                <ProgressHeaderLeft>
                  <ProgressIcon
                    color={fundraiser?.customColors.primary.hex || "#3b82f6"}
                  >
                    <Target size={18} />
                  </ProgressIcon>
                  <div>
                    <ProgressTitle>Fundraiser Progress</ProgressTitle>
                    {/* <ProgressSubtitle>10 supporters</ProgressSubtitle> */}
                  </div>
                </ProgressHeaderLeft>
              </ProgressHeader>

              <AmountRow>
                <CurrentAmount>
                  {formatCurrency(fundraiser?.totalRaisedAmount)}
                </CurrentAmount>
                <GoalAmount>of {formatCurrency(fundraiser?.goal)}</GoalAmount>
              </AmountRow>

              <ProgressBarContainer>
                <ProgressBar
                  percentage={fundraiserBarPercentage}
                  color={customColors.primary.hex || "#3b82f6"}
                  customColors={customColors}
                />
              </ProgressBarContainer>

              <ProgressFooter>
                <ProgressPercent
                  color={customColors.primary.hex || "#3b82f6"}
                  customColors={customColors}
                >
                  {actualFundraiserPercentage.toFixed(1)}% Complete
                </ProgressPercent>
                {fundraiser?.remainingAmount() > 0 && (
                  <RemainingAmount>
                    {formatCurrency(fundraiser?.remainingAmount())} to go
                  </RemainingAmount>
                )}
              </ProgressFooter>
            </ProgressCard>
          </>
        )}

        {actuallyShowPromoter && (
          <>
            <ProgressCard
              customColors={customColors}
              style={{ marginTop: shouldStack ? "16px" : "0" }}
            >
              <ProgressHeader>
                <ProgressIcon color={customColors.secondary.hex || "#10b981"}>
                  <User size={18} />
                </ProgressIcon>
                <ProgressTitleContainer>
                  <div style={{ flex: 1 }}>
                    <ProgressTitle>
                      {currentPromoter?.displayName}'s Progress
                    </ProgressTitle>
                    <ProgressSubtitle>
                      Personal fundraising goal
                    </ProgressSubtitle>
                  </div>
                  {currentPromoter &&
                    currentPromoter?.totalRaisedAmount > 0 && (
                      <RankBadge>#{currentPromoter.rank}</RankBadge>
                    )}
                </ProgressTitleContainer>
              </ProgressHeader>

              <AmountRow>
                <CurrentAmount>
                  {formatCurrency(currentPromoter?.totalRaisedAmount || 0)}
                </CurrentAmount>
                {currentPromoter && currentPromoter.goalAmount > 0 && (
                  <GoalAmount>
                    of {formatCurrency(currentPromoter.goalAmount)}
                  </GoalAmount>
                )}
              </AmountRow>

              <ProgressBarContainer>
                <ProgressBar
                  percentage={promoterBarPercentage}
                  color={customColors.secondary.hex || "#10b981"}
                  customColors={customColors}
                />
              </ProgressBarContainer>

              <ProgressFooter>
                {currentPromoter && currentPromoter.goalAmount > 0 && (
                  <ProgressPercent
                    color={customColors.secondary.hex || "#10b981"}
                    customColors={customColors}
                  >
                    {actualPromoterPercentage.toFixed(1)}% Complete
                  </ProgressPercent>
                )}
                {currentPromoter &&
                  currentPromoter.goalAmount > 0 &&
                  currentPromoter.goalAmount -
                  currentPromoter?.totalRaisedAmount >
                  0 && (
                    <RemainingAmount>
                      {formatCurrency(
                        currentPromoter.goalAmount -
                        currentPromoter?.totalRaisedAmount
                      )}{" "}
                      to go
                    </RemainingAmount>
                  )}
              </ProgressFooter>
            </ProgressCard>
          </>
        )}
      </ProgressContainer>
    );
  }
);
